<script setup>
/**
 * Everything WhatsApp knows about one piece of wording, and the things only WhatsApp can do
 * about it.
 *
 * ── WHY A PANEL AND NOT MORE COLUMNS ────────────────────────────────────────────────
 *
 * Meta's template object carries fifteen fields. A row that showed all of them would be
 * unreadable at forty templates and unusable at four hundred; a row that showed the five we
 * happened to think mattered is the screen this rebuild replaced. So the row carries what an
 * owner SCANS by — name, verdict, quality, the message itself — and this panel carries the rest,
 * one field per line, opened on the one template she is actually asking about.
 *
 * ── EVERY LINE IS EITHER A FACT OR AN ADMISSION ──────────────────────────────────────
 *
 * There are three ways a field can be empty here and they are three different sentences:
 *
 *   `fields === 'basic'`  WhatsApp answered, but this Graph version would not give us the
 *                         field at all. "WhatsApp did not send this" — our side is fine.
 *   value is null         WhatsApp gave us the field and it is empty. "WhatsApp hasn't said."
 *   value is a word we
 *   have no sentence for  Shown AS META WROTE IT, isolated in `<bdi>`.
 *
 * Rendering all three as a blank would be the same defect this whole feature exists to prevent,
 * one level down: a screen that looks like it knows.
 *
 * ── THE ACTIONS ARE THE ONES META ACTUALLY EXPOSES ──────────────────────────────────
 *
 * Unpause is an API call (`POST /{id}/unpause`). Delete is an API call. AN APPEAL IS NOT: Meta
 * publishes no appeal endpoint — for a refusal or for a category review — and says a review "can
 * only be requested via WhatsApp Manager". Unarchiving is the same: no documented endpoint, and a
 * 28-day window after which the template is gone for good. So those two are a DOOR to Meta's own
 * tool, not a button of ours that would have to fail. A button that cannot work is worse than a
 * link that does.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import TemplateAnalytics from './TemplateAnalytics.vue';

import { toIntlLocale } from '../helpers/intl.js';
import {
  KNOWN_BUTTON_TYPES,
  KNOWN_CATEGORIES,
  KNOWN_HEADER_FORMATS,
  KNOWN_REASONS,
  KNOWN_SUB_CATEGORIES,
  canAppeal,
  canDelete,
  canUnpause,
  describeTtl,
  readComponents,
  statusOf,
} from '../helpers/template-facts.js';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** The narrowed row from `api/templates.js`. */
  template: { type: Object, required: true },
  /** Which tier of Meta's field list answered: 'full' | 'core' | 'basic'. */
  fields: { type: String, default: 'basic' },
  /** Meta's own template manager, for the two things only it can do. */
  managerUrl: { type: String, default: '' },
  /** 'unpause' while that call is in flight, '' otherwise. */
  busy: { type: String, default: '' },
  /** For the performance panel, which fetches on demand. */
  accountId: { type: Number, default: null },
  inboxId: { type: String, default: '' },
});

const emit = defineEmits(['unpause', 'remove']);

const { t, locale } = useWasloI18n();

const parts = computed(() => readComponents(props.template));
const status = computed(() => statusOf(props.template));

/** A field this Graph version did not return at all, as opposed to one WhatsApp left empty. */
const unread = computed(() => props.fields === 'basic');

const untold = computed(() => t('WASLO.TEMPLATES.FACTS.UNTOLD'));
const unavailable = computed(() => t('WASLO.TEMPLATES.FACTS.NOT_SENT'));

/** Meta's word if we have a sentence for it, Meta's word itself if we do not, or an admission. */
const named = (value, list, prefix) => {
  if (!value) return unread.value ? unavailable.value : untold.value;
  const folded = String(value).toUpperCase();
  return list.includes(folded) ? t(`${prefix}.${folded}`) : String(value);
};

const category = computed(() =>
  named(props.template.category, KNOWN_CATEGORIES, 'WASLO.TEMPLATES.CATEGORY')
);

const subCategory = computed(() =>
  props.template.subCategory
    ? named(
        props.template.subCategory,
        KNOWN_SUB_CATEGORIES,
        'WASLO.TEMPLATES.SUB_CATEGORY'
      )
    : ''
);

const reason = computed(() =>
  props.template.rejectedReason
    ? named(
        props.template.rejectedReason,
        KNOWN_REASONS,
        'WASLO.TEMPLATES.REASON'
      )
    : ''
);

const format = computed(() =>
  t(
    props.template.parameterFormat === 'NAMED'
      ? 'WASLO.TEMPLATES.FACTS.FORMAT_NAMED'
      : 'WASLO.TEMPLATES.FACTS.FORMAT_NUMBERED'
  )
);

// Meta's `date` is unix SECONDS, not milliseconds — a thousandfold error here renders 1970.
const whenRated = computed(() => {
  const seconds = props.template.quality?.date;
  if (!seconds) return '';
  const when = new Date(seconds * 1000);
  if (Number.isNaN(when.getTime())) return '';
  return when.toLocaleDateString(toIntlLocale(locale.value), {
    dateStyle: 'medium',
  });
});

/**
 * The quality rating, its date, and the sentence for each. Meta's object is `{ score, date }`
 * and `UNKNOWN` is its own word for "no customer feedback yet" — which is a real answer and not
 * a missing one, so it gets its own sentence rather than the "hasn't said" fallback.
 */
const quality = computed(() => {
  if (!props.template.quality?.score) {
    return unread.value ? unavailable.value : untold.value;
  }
  const score = props.template.quality.score.toUpperCase();
  const key = { GREEN: 'HIGH', YELLOW: 'MEDIUM', RED: 'LOW' }[score];
  if (!key) return t('WASLO.TEMPLATES.QUALITY.UNKNOWN');
  const when = whenRated.value;
  return when
    ? t('WASLO.TEMPLATES.FACTS.QUALITY_AS_OF', {
        quality: t(`WASLO.TEMPLATES.QUALITY.${key}`),
        when: when,
      })
    : t(`WASLO.TEMPLATES.QUALITY.${key}`);
});

const deliveryWindow = computed(() => {
  const described = describeTtl(props.template.messageSendTtlSeconds);
  if (!described) {
    return unread.value
      ? unavailable.value
      : t('WASLO.TEMPLATES.FACTS.WINDOW_DEFAULT');
  }
  return t(`WASLO.TEMPLATES.FACTS.WINDOW.${described.unit}`, {
    count: described.value,
  });
});

const tracking = computed(() => {
  const value = props.template.ctaUrlLinkTrackingOptedOut;
  if (value === null) return '';
  return t(
    value
      ? 'WASLO.TEMPLATES.FACTS.TRACKING_OFF'
      : 'WASLO.TEMPLATES.FACTS.TRACKING_ON'
  );
});

/**
 * The line above the message — or, for a media header, WHAT KIND of media it is.
 *
 * A media header carries an uploaded asset handle rather than a URL, and a handle cannot be
 * rendered or resolved, so naming the kind is the only honest thing to show. A format Meta has
 * added since (its own field enum is already missing the GIF and PRODUCT formats its component
 * pages document) is shown as Meta wrote it.
 */
const headerLine = computed(() => {
  const header = parts.value.header;
  if (!header) return t('WASLO.TEMPLATES.FACTS.NO_HEADER');
  if (header.format === 'TEXT') {
    return header.text || t('WASLO.TEMPLATES.FACTS.NO_HEADER');
  }
  return KNOWN_HEADER_FORMATS.includes(header.format)
    ? t(`WASLO.TEMPLATES.FACTS.HEADER.${header.format}`)
    : header.format;
});

const buttonLines = computed(() =>
  parts.value.buttons.map(button => ({
    label: button.known
      ? t(`WASLO.TEMPLATES.BUTTON_TYPE.${button.type}`)
      : button.type,
    known: KNOWN_BUTTON_TYPES.includes(button.type),
    // What the customer taps, and where it goes. A copy-code button has no label by Meta's rule,
    // so its code is the only thing to show.
    detail:
      button.text ||
      button.code ||
      button.url ||
      button.phoneNumber ||
      button.flow ||
      '',
  }))
);
</script>

<template>
  <div
    class="flex flex-col gap-5 pt-4 mt-1 border-t border-n-weak"
    data-waslo-template-facts
  >
    <dl class="grid gap-x-6 gap-y-3 mb-0 sm:grid-cols-2">
      <div class="flex flex-col gap-0.5 min-w-0">
        <dt class="text-xs text-n-slate-10">
          {{ t('WASLO.TEMPLATES.FACTS.ID') }}
        </dt>
        <dd class="mb-0 font-mono text-sm break-all text-n-slate-12" dir="ltr">
          {{ template.id || untold }}
        </dd>
      </div>

      <div class="flex flex-col gap-0.5 min-w-0">
        <dt class="text-xs text-n-slate-10">
          {{ t('WASLO.TEMPLATES.FACTS.QUALITY') }}
        </dt>
        <dd
          class="mb-0 text-sm text-n-slate-12"
          dir="auto"
          data-waslo-template-quality-fact
        >
          {{ quality }}
        </dd>
      </div>

      <div class="flex flex-col gap-0.5 min-w-0">
        <dt class="text-xs text-n-slate-10">
          {{ t('WASLO.TEMPLATES.FACTS.CATEGORY') }}
        </dt>
        <dd class="mb-0 text-sm text-n-slate-12" dir="auto">
          <bdi>{{ category }}</bdi>
        </dd>
        <!--
          A re-categorisation changes what every send of this template costs, and it is the one
          fact on this panel an owner will be billed for not reading.
        -->
        <dd
          v-if="template.previousCategory"
          class="mb-0 text-xs leading-5 text-n-amber-11"
          data-waslo-template-previous-category
        >
          {{
            t('WASLO.TEMPLATES.FACTS.MOVED_FROM', {
              from: template.previousCategory,
            })
          }}
        </dd>
        <dd
          v-if="template.correctCategory"
          class="mb-0 text-xs leading-5 text-n-amber-11"
          data-waslo-template-correct-category
        >
          {{
            t('WASLO.TEMPLATES.FACTS.SHOULD_BE', {
              should: template.correctCategory,
            })
          }}
        </dd>
      </div>

      <div v-if="subCategory" class="flex flex-col gap-0.5 min-w-0">
        <dt class="text-xs text-n-slate-10">
          {{ t('WASLO.TEMPLATES.FACTS.SUB_CATEGORY') }}
        </dt>
        <dd class="mb-0 text-sm text-n-slate-12" dir="auto">
          <bdi>{{ subCategory }}</bdi>
        </dd>
      </div>

      <div class="flex flex-col gap-0.5 min-w-0">
        <dt class="text-xs text-n-slate-10">
          {{ t('WASLO.TEMPLATES.FACTS.BLANKS_FORMAT') }}
        </dt>
        <dd class="mb-0 text-sm text-n-slate-12">{{ format }}</dd>
      </div>

      <div class="flex flex-col gap-0.5 min-w-0">
        <dt class="text-xs text-n-slate-10">
          {{ t('WASLO.TEMPLATES.FACTS.WINDOW_LABEL') }}
        </dt>
        <dd class="mb-0 text-sm text-n-slate-12" data-waslo-template-window>
          {{ deliveryWindow }}
        </dd>
      </div>

      <div v-if="tracking" class="flex flex-col gap-0.5 min-w-0">
        <dt class="text-xs text-n-slate-10">
          {{ t('WASLO.TEMPLATES.FACTS.TRACKING') }}
        </dt>
        <dd class="mb-0 text-sm text-n-slate-12">{{ tracking }}</dd>
      </div>

      <div
        v-if="template.libraryTemplateName"
        class="flex flex-col gap-0.5 min-w-0"
      >
        <dt class="text-xs text-n-slate-10">
          {{ t('WASLO.TEMPLATES.FACTS.FROM_LIBRARY') }}
        </dt>
        <dd class="mb-0 font-mono text-sm break-all text-n-slate-12" dir="ltr">
          {{ template.libraryTemplateName }}
        </dd>
      </div>
    </dl>

    <!-- ── WHAT THE MESSAGE IS MADE OF ─────────────────────────────────────────── -->
    <div class="flex flex-col gap-2" data-waslo-template-parts>
      <p class="mb-0 text-xs text-n-slate-10">
        {{ t('WASLO.TEMPLATES.FACTS.PARTS') }}
      </p>
      <ul class="flex flex-col gap-1 mb-0 list-none">
        <li class="text-sm text-n-slate-11" dir="auto">
          <span class="text-n-slate-10">
            {{ t('WASLO.TEMPLATES.FACTS.HEADER_LABEL') }}
          </span>
          <bdi>{{ headerLine }}</bdi>
        </li>
        <li
          v-if="parts.footer?.text"
          class="text-sm text-n-slate-11"
          dir="auto"
        >
          <span class="text-n-slate-10">
            {{ t('WASLO.TEMPLATES.FACTS.FOOTER_LABEL') }}
          </span>
          <bdi>{{ parts.footer.text }}</bdi>
        </li>
        <!--
          An authentication template's wording is Meta's, not the owner's, so there is no body
          text to show — only the two choices she did make.
        -->
        <li
          v-if="parts.body.securityRecommendation"
          class="text-sm text-n-slate-11"
        >
          {{ t('WASLO.TEMPLATES.FACTS.SECURITY_LINE') }}
        </li>
        <li
          v-if="parts.footer?.codeExpirationMinutes"
          class="text-sm text-n-slate-11"
        >
          {{
            t('WASLO.TEMPLATES.FACTS.CODE_EXPIRY', {
              count: parts.footer.codeExpirationMinutes,
            })
          }}
        </li>
        <li v-if="parts.cards" class="text-sm text-n-slate-11">
          {{ t('WASLO.TEMPLATES.FACTS.CARDS', { count: parts.cards }) }}
        </li>
        <li v-if="parts.offer" class="text-sm text-n-slate-11" dir="auto">
          <bdi>
            {{ t('WASLO.TEMPLATES.FACTS.OFFER', { text: parts.offer.text }) }}
          </bdi>
        </li>
        <li
          v-for="(button, index) in buttonLines"
          :key="index"
          class="text-sm text-n-slate-11"
          dir="auto"
          :data-waslo-template-button-fact="index"
        >
          <span class="text-n-slate-10">{{ button.label }}</span>
          <bdi v-if="button.detail"> · {{ button.detail }}</bdi>
        </li>
        <li
          v-if="!buttonLines.length"
          class="text-sm text-n-slate-11"
          data-waslo-template-no-buttons
        >
          {{ t('WASLO.TEMPLATES.FACTS.NO_BUTTONS') }}
        </li>
      </ul>
    </div>

    <!--
      ── HOW IT PERFORMED ──────────────────────────────────────────────────────────
      The only part of this panel that is about results rather than permission, and the reason a
      marketer opens the screen at all.
    -->
    <TemplateAnalytics
      v-if="template.id"
      :account-id="accountId"
      :inbox-id="inboxId"
      :template="template"
    />

    <!-- ── WHAT WHATSAPP IS DOING, AND WHAT SHE CAN DO ─────────────────────────── -->
    <p
      v-if="reason"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-ruby-9"
      dir="auto"
      data-waslo-template-reason-full
    >
      {{ t('WASLO.TEMPLATES.ROW.REASON', { reason: reason }) }}
    </p>

    <p
      v-if="status === 'PAUSED'"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
      data-waslo-template-paused-note
    >
      {{ t('WASLO.TEMPLATES.LIFECYCLE.PAUSED') }}
    </p>
    <p
      v-if="status === 'DISABLED'"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
      data-waslo-template-disabled-note
    >
      {{ t('WASLO.TEMPLATES.LIFECYCLE.DISABLED') }}
    </p>
    <!--
      The 28-day clock. Meta archives a template nobody has used for twelve months and DELETES it
      28 days later, with no recovery — and unarchiving has no API, only its own Manager. This is
      the one notice on the screen with a deadline on it.
    -->
    <p
      v-if="status === 'ARCHIVED'"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-amber-11"
      data-waslo-template-archived-note
    >
      {{ t('WASLO.TEMPLATES.LIFECYCLE.ARCHIVED') }}
    </p>
    <p
      v-if="status === 'PENDING_DELETION'"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
      data-waslo-template-pending-deletion-note
    >
      {{ t('WASLO.TEMPLATES.LIFECYCLE.PENDING_DELETION') }}
    </p>

    <div class="flex flex-wrap items-center gap-2">
      <Button
        v-if="canUnpause(template)"
        :label="
          busy === 'unpause'
            ? t('WASLO.TEMPLATES.ACTIONS.UNPAUSING')
            : t('WASLO.TEMPLATES.ACTIONS.UNPAUSE')
        "
        icon="i-lucide-play"
        variant="faded"
        color="slate"
        size="sm"
        :is-loading="busy === 'unpause'"
        :disabled="busy !== ''"
        data-waslo-template-unpause
        @click="emit('unpause')"
      />
      <a
        v-if="canAppeal(template) || status === 'ARCHIVED'"
        :href="managerUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1.5 h-8 px-3 text-sm font-medium rounded-lg outline outline-1 -outline-offset-1 outline-n-weak text-n-slate-12 hover:bg-n-alpha-1"
        data-waslo-template-appeal
      >
        <span class="size-4 i-lucide-external-link" aria-hidden="true" />
        {{
          status === 'ARCHIVED'
            ? t('WASLO.TEMPLATES.ACTIONS.UNARCHIVE')
            : t('WASLO.TEMPLATES.ACTIONS.APPEAL')
        }}
      </a>
      <Button
        v-if="canDelete(template)"
        :label="t('WASLO.TEMPLATES.ACTIONS.REMOVE')"
        icon="i-lucide-trash-2"
        variant="ghost"
        color="ruby"
        size="sm"
        :disabled="busy !== ''"
        data-waslo-template-remove
        @click="emit('remove')"
      />
    </div>
  </div>
</template>
